import { useCallback, useEffect, useState } from "react";

/**
 * Per-collection reading progress, stored in localStorage.
 * Keyed by collection slug; value is a set of discovery slugs the reader
 * has opened. Used to power episode dots, "continue collection" CTAs,
 * and the /collections progress bars.
 */
const KEY = "sa.collprog.v1";

type Store = Record<string, string[]>;

function read(): Store {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(KEY) ?? "{}") as Store;
  } catch {
    return {};
  }
}

function write(s: Store) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota */
  }
}

export function useCollectionProgress(collectionSlug?: string | null) {
  const [visited, setVisited] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!collectionSlug) return;
    const all = read();
    setVisited(new Set(all[collectionSlug] ?? []));
  }, [collectionSlug]);

  const markVisited = useCallback(
    (discoverySlug: string) => {
      if (!collectionSlug || !discoverySlug) return;
      const all = read();
      const cur = new Set(all[collectionSlug] ?? []);
      if (cur.has(discoverySlug)) return;
      cur.add(discoverySlug);
      all[collectionSlug] = Array.from(cur);
      write(all);
      setVisited(cur);
    },
    [collectionSlug],
  );

  return { visited, markVisited };
}

/** Read all collection progress (sync, client-only). For /collections list. */
export function useAllCollectionProgress() {
  const [store, setStore] = useState<Store>({});
  useEffect(() => {
    setStore(read());
  }, []);
  return store;
}
